import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  Sun, Calendar, DollarSign, Users, GraduationCap, ShoppingCart,
  Clock, RefreshCw, Sparkles, TrendingUp, Award, ChevronRight,
  CheckCircle2, AlertTriangle, Tag, FileText, Target, Package
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import PulseCard from "../../components/PulseCard";
import MissedPunchCard from "../../components/MissedPunchCard";
import TimeOffCard from "../../components/TimeOffCard";

function fmtShortDate(iso) {
  try { return new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  catch { return iso; }
}

function PayPeriodWidget({ clock }) {
  if (!clock?.period_start) return null;
  const isHourly = clock.pay_type === "hourly";
  const totalHours = clock.total_period_hours ?? 0;
  const closedHours = clock.period_hours ?? 0;
  const liveHours = clock.live_hours ?? 0;
  const dailyMax = Math.max(1, ...(clock.daily_hours || []).map(d => d.hours || 0));
  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="pay-period-widget"
      className={`rounded-2xl border p-5 ${isHourly ? "bg-gradient-to-br from-indigo-50 via-white to-white border-indigo-100" : "bg-white border-slate-200"}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-bold uppercase tracking-widest ${isHourly ? "text-indigo-500" : "text-slate-400"} font-['Manrope']`}>
              This Pay Period
            </span>
            {isHourly && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-bold uppercase tracking-wider">Hourly</span>}
          </div>
          <p className="text-sm text-slate-600 font-['Manrope'] mt-0.5" data-testid="period-range">
            {fmtShortDate(clock.period_start)} — {fmtShortDate(clock.period_end)}
          </p>
        </div>
        {clock.clocked_in && (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-700 font-semibold font-['Manrope']">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            On the clock
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1 font-['Manrope']">Hours Logged</p>
          <p className="font-['Outfit'] text-3xl font-semibold text-slate-900 tabular-nums" data-testid="period-hours-value">
            {totalHours.toFixed(2)}<span className="text-lg text-slate-400 font-normal">h</span>
          </p>
          {liveHours > 0 && (
            <p className="text-[11px] text-slate-400 font-['Manrope'] mt-0.5">
              {closedHours.toFixed(2)}h closed · <span className="text-emerald-600">+{liveHours.toFixed(2)}h live</span>
            </p>
          )}
        </div>
        {isHourly ? (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-500 mb-1 font-['Manrope']">Est. Gross Pay</p>
            <p className="font-['Outfit'] text-3xl font-semibold text-indigo-600 tabular-nums" data-testid="period-pay-estimate">
              ${(clock.period_pay_estimate ?? 0).toFixed(2)}
            </p>
            <p className="text-[11px] text-slate-400 font-['Manrope'] mt-0.5">
              at ${Number(clock.hourly_rate).toFixed(2)}/hr
            </p>
          </div>
        ) : (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1 font-['Manrope']">Period Length</p>
            <p className="font-['Outfit'] text-3xl font-semibold text-slate-900 tabular-nums">
              {clock.period_length_days || 14}<span className="text-lg text-slate-400 font-normal">d</span>
            </p>
            <p className="text-[11px] text-slate-400 font-['Manrope'] mt-0.5">
              Paid on commission + tips
            </p>
          </div>
        )}
      </div>

      {/* Daily sparkline */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 font-['Manrope']">Daily Breakdown</p>
        <div className="flex gap-1" data-testid="period-daily-bars">
          {(clock.daily_hours || []).map(d => {
            const pct = d.hours > 0 ? Math.max((d.hours / dailyMax) * 100, 4) : 0;
            const isToday = d.date === todayStr;
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center" title={`${d.day} ${fmtShortDate(d.date)}: ${d.hours.toFixed(2)}h`}>
                <div className="h-12 w-full flex items-end">
                  <div className={`w-full rounded-sm transition-all ${isToday ? (isHourly ? "bg-indigo-500" : "bg-rose-400") : d.hours > 0 ? (isHourly ? "bg-indigo-200" : "bg-slate-300") : "bg-slate-100"}`}
                    style={{ height: `${pct || 4}%`, minHeight: pct ? "4px" : "2px" }} />
                </div>
                <span className={`text-[9px] mt-1 ${isToday ? "text-slate-900 font-semibold" : "text-slate-400"} font-['Manrope']`}>
                  {d.day.slice(0, 2)}
                </span>
                {d.hours > 0 && (
                  <span className={`text-[9px] ${isToday ? "text-slate-700 font-semibold" : "text-slate-400"} tabular-nums font-['Manrope']`}>
                    {d.hours.toFixed(1)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function StylistDayPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiSummary, setAiSummary] = useState("");
  const [loadingAi, setLoadingAi] = useState(false);
  const [clockLoading, setClockLoading] = useState(false);

  const fetchPortal = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/my/portal");
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load portal data");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPortal(); }, [fetchPortal]);

  const fetchAiBrief = async () => {
    setLoadingAi(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const res = await api.get(`/shift-briefing/ai-summary?provider_id=${user.user_id}&date=${today}`);
      setAiSummary(res.data?.summary || "");
    } catch {
      toast.error("Failed to generate brief");
    }
    setLoadingAi(false);
  };

  const handleClock = async (action) => {
    setClockLoading(true);
    try {
      await api.post(`/timeclock/${action}`, { user_id: user.user_id });
      toast.success(action === "clock-in" ? "Clocked in!" : "Clocked out!");
      fetchPortal();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clock action failed");
    }
    setClockLoading(false);
  };

  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }); }
    catch { return ""; }
  };
  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }); }
    catch { return ""; }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-5 h-5 animate-spin text-rose-400" />
      </div>
    );
  }

  const schedule = data?.schedule || {};
  const earnings = data?.earnings || {};
  const clock = data?.clock || {};
  const todayAppts = schedule.today || [];

  return (
    <div data-testid="stylist-day-page" className="p-6 lg:p-8 max-w-[1100px] mx-auto space-y-6">
      {/* Header with Clock */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">
            Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">
            {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div data-testid="clock-widget" className={`flex items-center gap-2 px-4 py-2 rounded-2xl border ${clock.clocked_in ? "bg-[#D4EDDA] border-[#C3E6CB]" : "bg-white border-slate-200"}`}>
            <Clock className={`w-4 h-4 ${clock.clocked_in ? "text-[#155724]" : "text-slate-400"}`} />
            <span className={`text-sm font-medium ${clock.clocked_in ? "text-[#155724]" : "text-slate-600"}`}>
              {clock.clocked_in ? `In since ${fmtTime(clock.clock_in_time)}` : "Not clocked in"}
            </span>
          </div>
          <Button
            size="sm"
            onClick={() => handleClock(clock.clocked_in ? "clock-out" : "clock-in")}
            disabled={clockLoading}
            className={clock.clocked_in ? "bg-rose-600 hover:bg-[#6B1F1A] text-white" : "bg-emerald-600 hover:bg-[#1E3F26] text-white"}
            data-testid="clock-toggle-btn"
          >
            {clockLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : clock.clocked_in ? "Clock Out" : "Clock In"}
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div data-testid="stat-today-appts" className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-[#8B5CF6]" />
            <span className="text-xs text-slate-400">Today</span>
          </div>
          <p className="font-['Outfit'] text-xl font-semibold text-slate-900">{schedule.today_count || 0} <span className="text-sm font-normal text-slate-400">appts</span></p>
        </div>
        <div data-testid="stat-today-rev" className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-emerald-600" />
            <span className="text-xs text-slate-400">Today Est.</span>
          </div>
          <p className="font-['Outfit'] text-xl font-semibold text-emerald-600">${schedule.today_revenue || 0}</p>
        </div>
        <div data-testid="stat-week-sales" className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-rose-400" />
            <span className="text-xs text-slate-400">7-Day Sales</span>
          </div>
          <p className="font-['Outfit'] text-xl font-semibold text-slate-900">${earnings.week_sales || 0}</p>
        </div>
        <div data-testid="stat-commission" className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-4 h-4 text-[#F59E0B]" />
            <span className="text-xs text-slate-400">{earnings.commission_tier || "Base"}</span>
          </div>
          <p className="font-['Outfit'] text-xl font-semibold text-slate-900">{Math.round((earnings.commission_rate || 0) * 100)}%</p>
        </div>
      </div>

      {/* Weekly Pulse */}
      <PulseCard userName={user?.name} />

      {/* Missed Punch Card */}
      <MissedPunchCard />

      {/* Time Off Card */}
      <TimeOffCard />

      {/* Pay Period Widget */}
      <PayPeriodWidget clock={clock} />

      {/* AI Brief */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sun className="w-5 h-5 text-[#F59E0B]" />
            <h2 className="font-['Outfit'] font-semibold text-slate-900">Today's Brief</h2>
          </div>
          {!aiSummary && (
            <Button size="sm" variant="outline" onClick={fetchAiBrief} disabled={loadingAi} className="border-slate-200 text-slate-600" data-testid="generate-brief-btn">
              <Sparkles className="w-4 h-4 mr-1" /> {loadingAi ? "Generating..." : "AI Brief"}
            </Button>
          )}
        </div>
        {aiSummary ? (
          <p data-testid="ai-brief-text" className="text-sm text-[#3D3D3D] leading-relaxed whitespace-pre-line">{aiSummary}</p>
        ) : (
          <p className="text-sm text-slate-400">
            You have <strong>{schedule.today_count || 0}</strong> appointments today with an estimated revenue of <strong>${schedule.today_revenue || 0}</strong>.
            {todayAppts.length > 0 && ` First up: ${todayAppts[0].client_name} at ${fmtTime(todayAppts[0].start_time)} for ${todayAppts[0].service_names.join(", ")}.`}
            {" "}Click "AI Brief" for a detailed morning summary.
          </p>
        )}
      </div>

      {/* Today's Schedule */}
      <div>
        <h2 className="font-['Outfit'] font-semibold text-slate-900 mb-3 text-sm uppercase tracking-wider">Today's Schedule</h2>
        {todayAppts.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
            <Calendar className="w-8 h-8 mx-auto mb-2 text-[#D1D1D1]" />
            <p className="text-slate-400">No appointments today</p>
          </div>
        ) : (
          <div className="space-y-2">
            {todayAppts.map((appt, i) => (
              <div key={appt.appointment_id} data-testid={`today-appt-${i}`} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4 hover:shadow-sm transition-shadow">
                <div className="text-center min-w-[60px]">
                  <p className="font-['Outfit'] text-sm font-semibold text-slate-900">{fmtTime(appt.start_time)}</p>
                  <p className="text-[10px] text-slate-400">{fmtTime(appt.end_time)}</p>
                </div>
                <div className="w-px h-10 bg-[#E6E4E0]" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 text-sm">{appt.client_name}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {appt.service_names.map((s, si) => (
                      <span key={`${appt.appointment_id}-svc-${si}`} className="text-xs bg-[#FAFAFA] text-slate-600 px-2 py-0.5 rounded border border-slate-200">{s}</span>
                    ))}
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-['Outfit'] font-semibold text-emerald-600 text-sm">${appt.total_price}</p>
                  <p className={`text-[10px] mt-0.5 ${appt.status === "confirmed" ? "text-emerald-600" : "text-slate-400"}`}>{appt.status}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upcoming */}
      {(schedule.upcoming || []).length > 0 && (
        <div>
          <h2 className="font-['Outfit'] font-semibold text-slate-900 mb-3 text-sm uppercase tracking-wider">Upcoming</h2>
          <div className="space-y-2">
            {schedule.upcoming.slice(0, 5).map((appt, i) => (
              <div key={appt.appointment_id} data-testid={`upcoming-appt-${i}`} className="bg-white border border-slate-200 rounded-2xl p-3 flex items-center gap-3">
                <div className="text-center min-w-[70px]">
                  <p className="text-xs font-medium text-slate-900">{fmtDate(appt.start_time)}</p>
                  <p className="text-[10px] text-slate-400">{fmtTime(appt.start_time)}</p>
                </div>
                <div className="w-px h-8 bg-[#E6E4E0]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">{appt.client_name}</p>
                  <p className="text-xs text-slate-400">{appt.service_names.join(", ")}</p>
                </div>
                <p className="text-sm font-semibold text-slate-600">${appt.total_price}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
